import React, { useEffect, useState, useRef } from "react";
import "./worker_documents.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SignatureCanvas from 'react-signature-canvas';

/* ===== FIREBASE ===== */
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc, arrayUnion, query, where } from "firebase/firestore";

const WorkerDocuments = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(); 
  
  const sigCanvas = useRef({}); 
  const [signingDocId, setSigningDocId] = useState(null);
  
  // --- PATAISYMAS: Dinaminis plotis ---
  // Pradžioje nustatome saugų plotį (280), o vėliau pritaikome pagal ekraną
  const [canvasWidth, setCanvasWidth] = useState(280); 

  const workerId = localStorage.getItem("workerId");
  const masterAdminId = localStorage.getItem("masterAdminId"); 
  const workerName = localStorage.getItem("workerName") || t('worker');

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Apskaičiuojame maksimalų galimą plotį telefonui
  useEffect(() => {
    const handleResize = () => {
        // Paimame ekrano plotį ir atimame 60px (kad liktų vietos kraštams)
        const w = window.innerWidth - 60;
        // Jei ekranas didelis, naudojame 320px, jei mažas - naudojame apskaičiuotą
        setCanvasWidth(w > 320 ? 320 : w);
    };

    handleResize(); // Paleidžiame iškart
    window.addEventListener('resize', handleResize); // Stebime jei pasuktų ekraną
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    const hasSession = localStorage.getItem("workerGateDone") === "true";

    if (!isRegistered) { navigate("/worker", { replace: true }); return; }
    if (!hasSession || !workerId) { navigate("/worker/login", { replace: true }); return; }

    const loadData = async () => {
        try {
            const q = query(
              collection(db, "company_docs"), 
              where("adminId", "==", masterAdminId)
            );
            
            const snap = await getDocs(q);
            const list = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(d => {
                    if (!d.assignedTo || d.assignedTo === 'all') return true;
                    if (Array.isArray(d.assignedTo) && d.assignedTo.includes(workerId)) return true;
                    return false;
                });

            list.sort((a, b) => {
                const signedA = a.signatures?.includes(workerId) ? 1 : 0;
                const signedB = b.signatures?.includes(workerId) ? 1 : 0;
                return signedA - signedB; 
            });

            setDocs(list);
        } catch (e) {
            console.error("Error loading docs:", e);
        }
        setLoading(false);
    };

    if (masterAdminId) loadData();
  }, [workerId, masterAdminId, navigate]);

  const saveSignature = async (docId) => {
      if (sigCanvas.current.isEmpty()) {
          alert(t('please_sign')); 
          return;
      }

      const signatureImage = sigCanvas.current.getCanvas().toDataURL("image/png");

      if(!window.confirm(t('confirm_sign_doc'))) return; 

      try {
          const docRef = doc(db, "company_docs", docId);
          const newSignatureData = {
              uid: workerId,
              name: workerName,
              signature: signatureImage,
              signedAt: new Date().toISOString()
          };

          await updateDoc(docRef, { 
              signatures: arrayUnion(workerId),
              signatures_data: arrayUnion(newSignatureData)
          });
          
          setDocs(prev => prev.map(d => (d.id === docId ? { 
            ...d, 
            signatures: [...(d.signatures || []), workerId],
            signatures_data: [...(d.signatures_data || []), newSignatureData]
          } : d)));

          setSigningDocId(null);
          alert("✅ " + t('signed_successfully'));

      } catch (e) { 
          console.error("Sign error:", e);
          alert(t('error') + ": " + e.message); 
      }
  };

  return (
    <div className="docs-page">
      <div className="docs-header">
        <button className="gold-back-btn" onClick={() => navigate("/worker/home", { replace: true })}>
              ← {t('back')}
        </button>
        <div className="header-info">
          <div className="worker-name">{workerName}</div>
          <div className="subtitle">{t('documents')}</div>
        </div>
      </div>

      <div className="docs-scroll">
        {loading && <div className="loading-state">{t('loading')}...</div>}
        {!loading && docs.length === 0 && <div className="empty-state">{t('no_documents')}</div>}

        {docs.map((docItem) => {
          const isSigned = docItem.signatures?.includes(workerId);
          const isSigningNow = signingDocId === docItem.id; 
          
          return (
            <div key={docItem.id} className={`doc-card ${isSigned ? 'is-signed' : 'needs-sig'}`}>
              <div className="doc-title">{docItem.title}</div>
              
              {docItem.fileUrl && (
                  <a href={docItem.fileUrl} target="_blank" rel="noreferrer" className="file-link">
                    📄 {docItem.fileName || t('open_file')}
                  </a>
              )}

              {docItem.content && <div className="doc-content">{docItem.content}</div>}

              <div className="doc-meta">
                <span className="date">{docItem.createdAt ? new Date(docItem.createdAt).toLocaleDateString() : ""}</span>
                <span className={`badge ${isSigned ? 'signed' : 'required'}`}>
                  {isSigned ? `✅ ${t('signed')}` : `❗ ${t('require_signature')}`}
                </span>
              </div>

              {isSigned && (
                <div className="your-sig-preview">
                  <span>{t('your_signature')}:</span>
                  <img src={docItem.signatures_data?.find(s => s.uid === workerId)?.signature} alt="Signature" />
                </div>
              )}

              {!isSigned && docItem.requireSignature && (
                <div className="doc-actions">
                  {!isSigningNow ? (
                    <button className="btn-accept" onClick={() => setSigningDocId(docItem.id)}>
                        ✍️ {t('sign_document')}
                    </button>
                  ) : (
                    <div className="signature-container-modal">
                        <p className="sig-instruction">{t('draw_signature')}</p>
                        
                        <div className="canvas-wrapper-fixed">
                            {/* Naudojame dinaminį canvasWidth */}
                            <SignatureCanvas 
                                ref={sigCanvas} 
                                penColor='black'
                                backgroundColor="white"
                                canvasProps={{ className: 'sigCanvasActual', width: canvasWidth, height: 180 }} 
                            />
                        </div>

                        <div className="sig-action-buttons">
                            <button onClick={() => sigCanvas.current.clear()} className="btn-secondary-clear">
                                🗑️ {t('clear')}
                            </button>
                            <button onClick={() => setSigningDocId(null)} className="btn-secondary-cancel">
                                ❌ {t('cancel')}
                            </button>
                        </div>
                        
                        <button onClick={() => saveSignature(docItem.id)} className="btn-confirm-sig-large">
                            ✅ {t('confirm_signature')}
                        </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkerDocuments;